import React, { useEffect, useRef, useState } from "react";
import "./PostsRecommendRow.scss";

import likeIcon from "./img/like.png";
import commentIcon from "./img/comment.png";
import repostIcon from "./img/arrow.png";
import shareIcon from "./img/share.png";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const getPostPhoto = (item) => {
  if (item?.photo) return item.photo;

  if (Array.isArray(item?.photos) && item.photos[0]) {
    return item.photos[0];
  }

  return "";
};

const getPostText = (item) => {
  return String(item?.text || item?.title || "").trim();
};

const formatCompactCount = (value) => {
  const n = Number(value || 0);

  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;

  return String(n);
};

const PostsRecommendRow = ({ items = [], onOpenPost, onMore }) => {
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const safeItems = Array.isArray(items) ? items.slice(0, 10) : [];

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [safeItems.length]);

  if (!safeItems.length) return null;

  const scrollPosts = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === "left" ? -600 : 600,
      behavior: "smooth",
    });

    setTimeout(updateScrollState, 420);
  };

  return (
    <section className="post-recs" aria-label="Рекомендовані пости">
      <div className="post-recs__head">
        <h3 className="post-recs__title">Пости з Cvanto</h3>
        <p className="post-recs__subtitle">
          Думки, оновлення та історії від людей
        </p>
      </div>

      <div className="post-recs__wrap">
        {canScrollLeft && (
          <button
            type="button"
            className="post-recs__arrow post-recs__arrow--left"
            onClick={() => scrollPosts("left")}
            aria-label="Прокрутити вліво"
          >
            ‹
          </button>
        )}

        <div className="post-recs__scroll" ref={scrollRef}>
          {safeItems.map((item) => {
            const id = String(item?.postId || item?.id || "");
            const text = getPostText(item);
            const photo = getPostPhoto(item);

            const authorName =
              item?.authorName ||
              item?.authorUsername ||
              item?.username ||
              "Користувач";

            const avatar = item?.authorAvatar || DEFAULT_AVATAR;

            const comments = item?.commentsCount || item?.stats?.comments || 0;
            const likes = item?.likesCount || item?.stats?.likes || 0;

            return (
              <article
                key={id}
                className={`post-recs__card ${
                  photo ? "post-recs__card--with-photo" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => onOpenPost?.(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpenPost?.(item);
                  }
                }}
              >
                <div className="post-recs__author">
                  <img
                    className="post-recs__avatar"
                    src={avatar}
                    alt={authorName}
                    loading="lazy"
                  />

                  <div className="post-recs__author-meta">
                    <strong>{authorName}</strong>
                    <span>пост</span>
                  </div>

                  <span className="post-recs__dots">•••</span>
                </div>

                {text && <div className="post-recs__text">{text}</div>}

                {photo && (
                  <div className="post-recs__photo-wrap">
                    <img
                      className="post-recs__photo"
                      src={photo}
                      alt=""
                      loading="lazy"
                    />
                  </div>
                )}

                <div
                  className="post-recs__actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="post-recs__icon-btn"
                    aria-label="Лайк"
                  >
                    <img src={likeIcon} alt="" />
                  </button>

                  <button
                    type="button"
                    className="post-recs__icon-btn"
                    aria-label="Коментар"
                  >
                    <img src={commentIcon} alt="" />
                  </button>

                  <button
                    type="button"
                    className="post-recs__icon-btn"
                    aria-label="Репост"
                  >
                    <img src={repostIcon} alt="" />
                  </button>

                  <button
                    type="button"
                    className="post-recs__icon-btn"
                    aria-label="Поділитися"
                  >
                    <img src={shareIcon} alt="" />
                  </button>
                </div>

                <div className="post-recs__stats">
                  <span>{formatCompactCount(comments)} ком.</span>
                  <span>·</span>
                  <span>{formatCompactCount(likes)} впод.</span>
                </div>
              </article>
            );
          })}

          <button type="button" className="post-recs__more" onClick={onMore}>
            <span>Більше постів</span>
            <b>→</b>
          </button>
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="post-recs__arrow post-recs__arrow--right"
            onClick={() => scrollPosts("right")}
            aria-label="Прокрутити вправо"
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
};

export default PostsRecommendRow;